import { useState, useCallback, useContext } from "react";
import { Dropdown } from "~/components/UI";
import EvPortsContext from "./evPortsContext";
import { useTranslation } from "react-i18next";
import { PERMISSION } from "~/api/permissions/enums";
import usePermissions from "~/hook/usePermissions";

interface EvPortOptionsProps {
  evPort: Api.EvPortDto;
  hideEditOptions?: boolean;
}

/**
 * @memberof EvPorts
 * @component
 * @desc Options for a single EV Port mapping. Using EvPortsContext.
 * @property {Api.EvPortDto} evPort - EV Port data;
 * @requires EvPortsContext
 */

const EvPortOptions = ({ evPort, hideEditOptions }: EvPortOptionsProps) => {
  const [loading, setLoading] = useState(false);
  const { onEdit, onDelete } = useContext(EvPortsContext);
  const { hasAll, hasAny } = usePermissions();
  const { t } = useTranslation();

  const onUpdateHandler = useCallback((res: Promise<unknown> | void) => {
    if (res) {
      setLoading(true);
      res.finally(() => {
        setLoading(false);
      });
    }
  }, []);

  if (
    !hasAny([
      PERMISSION.EDIT_EV_PORT,
      PERMISSION.DELETE_EV_PORT,
    ])
  ) {
    return null;
  }

  const options = [
    {
      label: t("option.edit"),
      hidden: !hasAll(PERMISSION.EDIT_EV_PORT) || hideEditOptions,
      onClick: () => onEdit && onEdit(evPort),
    },
    {
      label: t("option.delete"),
      hidden: !hasAll(PERMISSION.DELETE_EV_PORT) || hideEditOptions,
      onClick: () => onDelete && onUpdateHandler(onDelete(evPort)),
    },
  ];

  return <Dropdown loading={loading} options={options} />;
};

export default EvPortOptions;
